<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <not-bookable-error v-if="initStore.isNotBookable" :message="initStore.notBookableReason!" />

  <template v-else>
    <booking-result v-if="isBookingFinished" />
    <v-card v-else class="main-card">
      <v-card-title class="px-0 pt-0">
        <div v-if="stepsStore.steps" class="d-flex">
          <div
            v-for="(step, index) in stepsStore.steps"
            :key="step.sequence"
            class="step px-5 py-3"
            :class="[
              {
                active: step.isActive,
                disabled: step.isDisabled,
                completed: step.isCompleted,
                beforeActive: index < stepsStore.steps.length - 1
                  ? stepsStore.steps[index + 1]!.isActive
                  : false,
              },
            ]"
          >
            <span class="step-title px-4"> {{ step.title }} </span>
            <div class="arrow" />
          </div>
        </div>
      </v-card-title>

      <v-card-text v-if="isLoading" class="booking-card pt-4 px-6">
        <KeepAlive exclude="step-summary-and-booking">
          <component
            :is="activeStepComponent"
            ref="activeStepRef"
          />
        </KeepAlive>
      </v-card-text>

      <v-card-actions v-if="checkingData">
        <v-progress-linear indeterminate color="primary" />
      </v-card-actions>

      <v-card-actions v-else-if="stepsStore.steps">
        <v-switch
          v-if="allowDisableValidation"
          v-model="disableValidation"
          inset
          label="Skip validations"
          hide-details
          density="compact"
        />

        <v-spacer />

        <v-btn
          v-if="stepsStore.hasPreviousStep"
          variant="text"
          @click="goToPrevious()"
        >
          <v-icon class="px-4">
            mdi-arrow-left-bold
          </v-icon>
          Zurück
        </v-btn>

        <v-btn
          v-if="stepsStore.hasNextStep"
          variant="flat"
          @click="goToNext"
        >
          Weiter
          <v-icon class="px-4">
            mdi-arrow-right-bold
          </v-icon>
        </v-btn>

        <v-btn v-else variant="elevated" :disabled="lastStepButtonDisabled" @click="save()">
          {{ lastStepButtonText }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <pop-up-dialog v-if="showInfoDialog" :configs="infoDialogConfigs" @close="closePopUp()" />
  </template>
</template>

<script setup lang="ts">
import axios from 'axios';
import {
  computed,
  provide,
  ref,
  shallowRef,
} from 'vue';
import {
  BookingMode,
  type BookingModeFunc,
  type Order,
  type UpdateFinishButtonFunc,
} from '../types/order-main';
import {
  getPresignedUrl,
  order,
} from '../services/api';
import logger from '../services/logger';
import BookingResult from './booking-result.vue';
import NotBookableError from './not-bookable-error.vue';
import PopUpDialog from './pop-up-dialog.vue';
import { UpdateBookingModeKey, UpdateFinishButtonKey } from '../configs/symbols';
import useBookingStore from '../store/booking';
import useFormOrderRequestStore from '../store/form-order-request';
import useInitStore from '../store/init';
import useOrderStore from '../store/order';
import useStepsStore, { setActive, setCompleted, resetStep } from '../store/steps';

const bookingStore = useBookingStore();
const initStore = useInitStore();
const orderStore = useOrderStore();
const stepsStore = useStepsStore();

const activeStepRef = shallowRef();
const isLoading = ref<boolean>(false);
const checkingData = ref<boolean>(false);
const disableValidation = ref<boolean>(false);
const lastStepButtonText = ref<string>('Angebot fixieren');
const lastStepButtonDisabled = ref<boolean>(true);
const isBookingFinished = ref<boolean>(false);
const infoDialogConfigs = ref();
const showInfoDialog = ref<boolean>(false);

const allowDisableValidation = computed(() => import.meta.env.VITE_ALLOW_SKIP_VALIDATION === 'true');

const activeStepComponent = computed(() => stepsStore.getActiveStep()?.component);

async function getDocumentName(file: File): Promise<{ isError: boolean; result: string }> {
  const [uploadUrlError, uploadResult] = await getPresignedUrl(file.name.split('.').pop()!);
  if (uploadResult?.url !== 'offline') {
    await axios.put(uploadResult!.url, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  }
  return {
    isError: !!uploadUrlError,
    result: (uploadUrlError ? uploadUrlError.data.message : uploadResult?.fileName) || 'NO NAME',
  };
}

/**
 * Save the selection of the user in the order process
 * order info @param {Order} order is called from the child component (step-summary-and-booking.vue)
 * workflow is like this:
 * 1. user clicks on "Abschließen/Angebot fixieren" button in the last step,
 * emitting the event "save" to the parent component (order-main.vue)
 * 2. save method first makes validation of the data and if everything is ok
 * 3. depending on what is booked (house connection, ne4, product) the corresponding document is
 * uploaded to the s3 bucket wia presigned url
 * 4. after the document is uploaded the document name is set to the order param
 * 5. the order is created
 */
async function save() {
  const error = ref();
  const isReadyForOrder = await activeStepRef.value?.isValid();
  if (isReadyForOrder) {
    checkingData.value = true;
    const {
      orderParam,
      houseConnectionOrderDocument,
      productOrderDocument,
    } = await activeStepRef.value.getData();

    if ((orderParam.hasHouseConnection || orderParam.hasMph) && houseConnectionOrderDocument) {
      const houseConnectionUpload = await getDocumentName(houseConnectionOrderDocument);

      if (houseConnectionUpload.isError) {
        checkingData.value = false;
        error.value = houseConnectionUpload.result;
      } else {
        orderParam.houseConnectionDocumentName = houseConnectionUpload.result;
      }
    }

    if (orderParam.hasProduct && productOrderDocument) {
      const productUpload = await getDocumentName(productOrderDocument);

      if (productUpload.isError) {
        checkingData.value = false;
        error.value = productUpload.result;
      } else {
        orderParam.productDocumentName = productUpload.result;
      }
    }

    const [errorOrder, orderResult] = await order(orderParam as Order);
    if (errorOrder) {
      error.value = errorOrder;
    }

    if (error.value) {
      checkingData.value = false;

      const {
        errors,
        message,
      } = error.value.data;

      const dialogMessage = errors ? JSON.stringify(errors) : message;

      infoDialogConfigs.value = {
        title: 'Fehler beim abschließen des Auftrags',
        message: `Leider ist ein Fehler beim abschließen des Auftrags aufgetreten: ${dialogMessage}`,
        closeText: 'Schließen',
        closeIcon: 'close-thick',
        type: 'warning',
      };
      showInfoDialog.value = true;

      await logger.error({ message: 'failed to place order', context: { error: error.value } });

      setTimeout(() => {
        window.top?.postMessage({ eventName: 'orderUnsuccessful', ...errorOrder }, '*');
      }, 2000);
    } else {
      isBookingFinished.value = true;
      setTimeout(() => {
        window.top?.postMessage({ eventName: 'orderFinished', ...orderResult }, '*');
      }, 2000);
    }
  }
}

function closePopUp() {
  showInfoDialog.value = false;
  infoDialogConfigs.value = null;
}

function updateBookingMode(mode: BookingMode) {
  bookingStore.updateMode(mode);
  stepsStore.configureSteps(mode);
}

function nextAvailable(currentSequence: number): number {
  const targetIndex = stepsStore.steps.findIndex(step => step.sequence === currentSequence) + 1;
  return stepsStore.sequences[targetIndex]!;
}

function prevAvailable(currentSequence: number): number {
  const targetIndex = stepsStore.steps.findIndex(step => step.sequence === currentSequence) - 1;
  return stepsStore.sequences[targetIndex]!;
}

async function goToNext() {
  checkingData.value = true;
  const isStepValid = await activeStepRef.value?.isValid();
  if (!isStepValid && !disableValidation.value) {
    checkingData.value = false;
    return;
  }
  await activeStepRef.value?.applyData();
  const activeStep = stepsStore.getActiveStep();
  if (!activeStep) {
    return;
  }
  const nextStep = stepsStore.stepBySequence(nextAvailable(activeStep.sequence));
  checkingData.value = false;
  if (nextStep) {
    setActive(nextStep);
    setCompleted(activeStep);
  }
}

function goToPrevious() {
  const activeStep = stepsStore.getActiveStep();
  if (!activeStep) {
    return;
  }
  const nextStep = stepsStore.stepBySequence(prevAvailable(activeStep.sequence));
  if (nextStep) {
    setActive(nextStep);
    resetStep(activeStep);
  }
}

function updateFinishButton(text: string, disabled: boolean) {
  lastStepButtonText.value = text;
  lastStepButtonDisabled.value = disabled;
}

function openInfoDialog(title: string, message: string): void {
  infoDialogConfigs.value = {
    title,
    message,
    closeText: 'Verstanden',
    closeIcon: 'close-thick',
    type: 'warning',
  };
  showInfoDialog.value = true;
}

async function readUrlQuery() {
  const validation = await initStore.validateUrlQuery(allowDisableValidation.value);
  if (validation.isError) {
    await logger.warning({ message: 'invalid url query', context: { error: validation.error } });
    return;
  }

  updateBookingMode(initStore.initialBookingMode!);
}

async function loadHouseholdData() {
  await initStore.loadHousehold();

  if (initStore.hasError) {
    openInfoDialog(
      'Warnung: Kunden-ID konnte nicht gefunden werden',
      'Leider konnte die von Ihnen übergebene Kunden-ID nicht gefunden werden. Sie können allerdings die Bestellstrecke nutzen, um einen Neukunden-Auftrag anzulegen.',
    );
  }
}

async function setInitialStepsData() {
  const {
    bookingApproach,
    household,
    initialBookingMode,
    isNotBookable,
    params,
  } = initStore;

  if (isNotBookable) {
    return;
  }
  const formOrderRequestStore = useFormOrderRequestStore();
  formOrderRequestStore.isProductOrderBlocked = !bookingApproach!.isProductBookingPossible;
  switch (initialBookingMode) {
    case BookingMode.houseConnectionAndProduct: {
      const { isMphBookingPossible } = bookingApproach ?? {};
      await bookingStore.loadArticles();
      formOrderRequestStore.setData({
        blockMphBooking: isMphBookingPossible === false,
        blockProductBooking: true,
        bookingWithProduct: true,
        connectionPoint: '',
        iban: '',
        isMphOrder: false,
        ne4Order: '',
        price: 0,
        wantsBankTransfer: false,
      });
      orderStore.addOrderRequestData(formOrderRequestStore.stepData);
      break;
    }
    case BookingMode.productOnly:
      if (!params?.householdId) {
      // better if we use NotBookableError not dialog
        openInfoDialog(
          'Fehler: Kunden-ID konnte nicht gefunden werden',
          'Für den Buchungsmodus Product-only wird eine gültige Kunden-ID benötigt. Die Buchung kann daher nicht durchgeführt werden.',
        );
      } else if (!bookingApproach?.isProductBookingPossible) {
        openInfoDialog(
          'Fehler: Produkt wurde bereits gebucht',
          'Das ausgewählte Produkt wurde bereits gebucht. Die Buchung kann daher nicht durchgeführt werden.',
        );
      } else if (household) {
        const { houseConnectionBooked, ne4Booked } = bookingApproach!;

        let isMphOrder: boolean = ne4Booked === true;

        if (!houseConnectionBooked && !ne4Booked) {
          // We can only end up here if the target address
          // is an MPH address and there is no infrastructure yet.
          updateBookingMode(BookingMode.productOnlyMph);
          isMphOrder = true;
        }

        await bookingStore.loadArticles();

        formOrderRequestStore.setData({
          blockMphBooking: true,
          blockProductBooking: true,
          bookingWithProduct: true,
          connectionPoint: '',
          iban: '',
          isMphOrder,
          ne4Order: '',
          price: 0,
          wantsBankTransfer: false,
        });
        orderStore.addOrderRequestData(formOrderRequestStore.stepData);
      }
      break;
    case BookingMode.ne4Only: {
      const { isProductBookingPossible } = bookingApproach ?? {};
      const blockProductBooking = isProductBookingPossible === undefined
        ? false
        : !isProductBookingPossible;

      formOrderRequestStore.setData({
        blockMphBooking: false,
        blockProductBooking,
        bookingWithProduct: false,
        connectionPoint: '',
        iban: '',
        isMphOrder: true,
        ne4Order: '',
        price: 0,
        wantsBankTransfer: false,
      });
      orderStore.addOrderRequestData(formOrderRequestStore.stepData);
      break;
    }
    default:
      break;
  }
}

provide<UpdateFinishButtonFunc>(UpdateFinishButtonKey, updateFinishButton);
provide<BookingModeFunc>(UpdateBookingModeKey, updateBookingMode);

await readUrlQuery();
await initStore.loadBookingMetadata();
await loadHouseholdData();
await setInitialStepsData();
isLoading.value = true;
</script>
<style lang="scss" scoped>
  @use '../assets/sass/stepper.scss';
</style>
